import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Package } from 'lucide-react';
import WasslaImage from './WasslaImage';
import './MediaCarousel.css';

/**
 * iter330 — MediaCarousel
 *
 * Premium swipe-carousel for ad listing cards.
 *
 *   • Native CSS `scroll-snap` track — every slide is a `<WasslaImage>`
 *     in a flex item that snaps to the card width. No JS per frame,
 *     no virtual scrolling, no third-party deps. Touch + trackpad
 *     work out of the box.
 *   • Single-image ads short-circuit to a plain `<WasslaImage>`; no
 *     dot row, no pill, no overhead.
 *   • Multi-image ads render:
 *       — index pill ("1/4") top-corner (start-inset for RTL safety)
 *       — tiny dots row bottom-center (max 6 dots + "…")
 *   • Lazy-loading: only the first slide is rendered with
 *     `priority`. Every other slide inherits WasslaImage's default
 *     `loading="lazy"`. So the card grid never blocks on N images.
 *   • Card layout stable — the wrapper reserves the parent's
 *     aspect-ratio, and slides never overflow.
 *   • Heart / favorite button (rendered by the parent over the
 *     image area) is positioned with `pointer-events: auto` so it
 *     stays tappable even while the carousel is scrollable.
 *
 * Why CSS scroll-snap instead of a JS swiper?
 *   — One less library to ship. Native scroll on iOS/Android feels
 *     natural and respects platform gestures. JS swipers tend to
 *     hijack vertical scroll, which is what the operator's last
 *     marketplace UX bug report explicitly flagged.
 *
 * Props
 * -----
 *   images        string[]      — image URLs. Empty → Package placeholder.
 *   title         string        — used for alt text.
 *   adId          string        — used in data-testid for QA hooks.
 *   onImageLoad   fn?           — first-slide load handler (legacy).
 *   onImageError  fn?           — first-slide error handler (legacy).
 *   priorityFirst bool?         — passes `priority` to first slide.
 *                                 Default true.
 */
export default function MediaCarousel({
  images,
  title = '',
  adId,
  onImageLoad,
  onImageError,
  priorityFirst = true,
}) {
  const safeImages = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []),
    [images]
  );

  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  // Compute the current slide based on scroll position. Debounced via
  // rAF so we don't thrash setState on every scroll event.
  const rafRef = useRef(null);
  const onScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const el = trackRef.current;
      if (!el) return;
      const slideW = el.clientWidth || 1;
      const idx = Math.round(Math.abs(el.scrollLeft) / slideW);
      const clamped = Math.max(0, Math.min(safeImages.length - 1, idx));
      setActive((prev) => (prev === clamped ? prev : clamped));
    });
  }, [safeImages.length]);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  // Zero-image fallback. Keep the existing card placeholder pattern
  // so we don't introduce a new visual layer.
  if (safeImages.length === 0) {
    return (
      <div className="mediaCarousel mediaCarousel--empty" data-testid={`media-carousel-empty-${adId || 'unknown'}`}>
        <div className="mediaCarousel__placeholder" aria-hidden="true">
          <Package size={36} />
        </div>
      </div>
    );
  }

  // Single-image fast path. No carousel chrome at all.
  if (safeImages.length === 1) {
    return (
      <WasslaImage
        src={safeImages[0]}
        alt={title || 'Product'}
        fallbackType="ad"
        testid={`wassla-image-${adId || 'unknown'}`}
        onError={onImageError}
        onLoad={onImageLoad}
        showCaption={false}
        priority={priorityFirst}
      />
    );
  }

  const displayDots = Math.min(safeImages.length, 6);
  const hasMore = safeImages.length > 6;

  return (
    <div
      className="mediaCarousel"
      data-testid={`media-carousel-${adId || 'unknown'}`}
      data-count={safeImages.length}
    >
      <div
        className="mediaCarousel__track"
        ref={trackRef}
        onScroll={onScroll}
        // Stop touch from bubbling to the card click handler. The
        // browser already distinguishes scroll from tap, but on some
        // Android browsers the click still fires after a tiny swipe.
        // Cancelling propagation here keeps the card from accidentally
        // navigating mid-swipe.
        onTouchEnd={(e) => e.stopPropagation()}
      >
        {safeImages.map((src, i) => (
          <div className="mediaCarousel__slide" key={`${src}-${i}`}>
            <WasslaImage
              src={src}
              alt={title ? `${title} ${i + 1}/${safeImages.length}` : `Image ${i + 1}`}
              fallbackType="ad"
              testid={`wassla-image-${adId || 'unknown'}-${i}`}
              onError={i === 0 ? onImageError : undefined}
              onLoad={i === 0 ? onImageLoad : undefined}
              showCaption={false}
              priority={i === 0 && priorityFirst}
            />
          </div>
        ))}
      </div>

      <div
        className="mediaCarousel__pill"
        data-testid={`media-carousel-index-${adId || 'unknown'}`}
        aria-hidden="true"
      >
        {active + 1}/{safeImages.length}
      </div>

      <div
        className="mediaCarousel__dots"
        role="tablist"
        aria-label="Image carousel"
        data-testid={`media-carousel-dots-${adId || 'unknown'}`}
      >
        {Array.from({ length: displayDots }).map((_, i) => (
          <span
            key={i}
            className={`mediaCarousel__dot ${i === active ? 'is-active' : ''}`}
            aria-current={i === active ? 'true' : undefined}
          />
        ))}
        {hasMore && <span className="mediaCarousel__dotMore">…</span>}
      </div>
    </div>
  );
}
